import { BohemiaCodeLoaderConfig } from '@ms/bohemia-codeloader';
import { DriverInfo } from './DriverInfo';
import { HostConfig, OfficeWebHostScope } from './OfficeWebHostTypes';
import { LoadInIframeResult } from './Iframe-shared/LoadInIframeResult';
import { loadComponentInSourcelessIframeInternal } from './Iframe-sourceless/Outside/loadComponentInSourcelessIframeInternal';

export interface SourcelessIframeLoadOptions {
  /**
   * The container environment to load. Default is "Prod".
   **/
  containerEnvironment?: 'Prod' | 'Dogfood';

  /**
   * [For internal or testing use only]
   * A code-loader config override. Will take precedence over other code-loader configurations (e.g., over containerEnvironment)
   */
  DoNotUse_codeLoaderConfigOverride?: BohemiaCodeLoaderConfig;

  /**
   * Title for iframe element to be read by screen readers.
   */
  // Task 4485007: When we are ready to bump OWH version, remove the "?​​" from the parameter below to make it required
  iframeTitle?: string;

  /**
   * The path to the bundle that will be loaded inside the iframe. This is typically injected via a Webpack DefinePlugin.
   *
   * This parameter is not required yet -- if left empty, we will load from our CDN -- but in a future version of
   * Office Web Host, partners will be required to supply their own. Learn more by searching for `sourcelessIframeBundleUrl`
   * in <https://office.visualstudio.com/OC/_wiki/wikis/Fluid%20Framework%20Experiences/15005/OfficeWebHost>.
   */
  // Task 4883749: When we are ready to bump OWH version, remove the "?​​" from the parameter below to make it required,
  // and also remove the comment in the JSDOC that starts with "This parameter is not required yet" and ends in "supply their own."
  sourcelessIframeBundleUrl?: string;

  /**
   * Sourceless iframes have the ability to know when they are being removed from the DOM and as a result can do any memory cleanups on their own.
   * Exposing a dispose method here if host apps want the iframe to do some custom cleanup on their behalf
   */
  onDispose?: () => void;
}

export interface SourcelessIframeLoadOptionsForTesting extends SourcelessIframeLoadOptions {
  /**
   * For testing only, a string of JS code to inject into the iframe (e.g., to be able to test error conditions, etc.).
   * This code is executed after the initialize method on the inside of the iframe has been called
   * (but before the container and component promises have resolved).
   */
  __DoNotUse_codeToInjectForTestingOnly?: string;
}

/**
 * Loads a component within a sourceless iframe, into a provided hosting div,
 * given appropriate information and auth callbacks.
 * Failure to load will result in promise rejection with information
 * about the error.
 * @param driverInfo Driver specific information needed for performing Fluid load operations
 * @param componentDiv A div or a promise to a div that the component will be rendered into
 * @param scope An OfficeWebHostScope object that will be available to all containers/components loaded
 * @param config Additional information allowing a host to have richer integration with how components are initialized
 * @param options The sourceless iframe load options
 */
export function loadComponentInSourcelessIframe(
  driverInfo: DriverInfo,
  componentDiv: HTMLElement,
  scope: OfficeWebHostScope,
  config: HostConfig = {},
  // Task 4485007 and Task 4883749: When we are ready to bump OWH version, remove the "= {​​}​​" from the parameter below
  options: SourcelessIframeLoadOptions = {}
): LoadInIframeResult {
  // The moment we should consider the start of office-web-host execution, which is used for end to end performance analysis.
  const bootstrapperStartTime = Date.now();

  return loadComponentInSourcelessIframeInternal({ type: 'Load', driverInfo }, componentDiv, scope, config, options, {
    bootstrapperStartTime
  });
}

// cspell:ignore bootstrapper
